import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ClipboardList, Plus, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  Planned: 'bg-blue-100 text-blue-700',
  Ongoing: 'bg-yellow-100 text-yellow-700',
  Completed: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
};

export default function TripsPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await axios.get(`${API}/trips`);
      setTrips(res.data);
    } catch {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <ClipboardList className="text-pink-500" size={32} />
            Trips
          </h1>
          <p className="text-slate-500 mt-1">Manage school trips and excursions</p>
        </div>
        <Link to="/trips/new">
          <Button className="bg-pink-500 hover:bg-pink-600 text-white btn-press">
            <Plus size={18} className="mr-2" />
            Create Trip
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">All Trips</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : trips.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No trips yet. Create your first trip to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map(trip => (
                  <TableRow key={trip.id}>
                    <TableCell className="font-medium">{trip.title}</TableCell>
                    <TableCell>{trip.location}</TableCell>
                    <TableCell>{trip.trip_date}</TableCell>
                    <TableCell>${Number(trip.fee_per_student).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={statusColor[trip.status]}>
                        {trip.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/trips/${trip.id}`}>
                        <Button size="sm" variant="outline" className="text-pink-500 border-pink-200 hover:bg-pink-50">
                          <Eye size={14} className="mr-1" />
                          Manage
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
